import { useState, useCallback, useRef, useEffect } from 'react';
import type { ConversationState, AgentMessage, ConversationPhase } from '../types';
import type { FormValues } from '../../promotions/types';
import { templates } from '../../promotions/data/templates';
import { templateSteps } from '../data/questions';
import type { ConversationStep } from '../types';
import { validateDates } from '../../shared/utils/dateUtils';
import { getAgentAnswerError, normalizeAgentValues, validateAgentValues } from '../utils/conversationRules';

const TYPING_DELAY = 700;

function generateId() {
  return Math.random().toString(36).slice(2);
}

function agentMsg(
  content: string,
  extras: Partial<AgentMessage> = {},
): AgentMessage {
  return {
    id: generateId(),
    role: 'agent',
    content,
    timestamp: Date.now(),
    ...extras,
  };
}

function userMsg(content: string, fieldKey?: string): AgentMessage {
  return {
    id: generateId(),
    role: 'user',
    content,
    timestamp: Date.now(),
    fieldKey,
  };
}

const GREETING_MESSAGES: AgentMessage[] = [
  agentMsg(
    'Olá! Sou o **Agente de Promoções** 👋 Estou aqui para te ajudar a preencher o briefing de criação de promoção de forma rápida e guiada.',
  ),
  agentMsg(
    'Qual **tipo de mecânica promocional** você deseja criar?',
    {
      quickReplies: templates.map((t) => ({
        label: `${t.number} — ${t.name}`,
        value: t.id,
      })),
    },
  ),
];

const EMPTY_FIELDS = ['-', '', 'n/a', 'pular', 'skip'];

function resolveDisplayText(input: string, step: ConversationStep | undefined): string {
  if (!step?.options || !step.options.length) return input;
  const values = input.split(',').map((v) => v.trim()).filter(Boolean);
  const labels = values.map((v) => {
    const found = step.options!.find((o) => o.value === v);
    return found ? found.label : v;
  });
  return labels.join(', ');
}

interface PendingAdvance {
  currentIndex: number;
  steps: ConversationStep[];
  values: FormValues;
}

export function useAgentConversation() {
  const [state, setState] = useState<ConversationState>({
    phase: 'greeting',
    messages: GREETING_MESSAGES,
    selectedTemplateId: null,
    formValues: { tipoSolicitacao: '', idIFC: 'n/a' },
    currentStepIndex: 0,
    steps: [],
    isTyping: false,
    fieldErrors: {},
  });

  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingAdvance = useRef<PendingAdvance | null>(null);

  const setTyping = useCallback((typing: boolean) => {
    setState((prev) => ({ ...prev, isTyping: typing }));
  }, []);

  const advanceToNextStep = useCallback(
    (
      currentIndex: number,
      steps: ConversationStep[],
      values: FormValues,
      phase: ConversationPhase,
    ) => {
      if (phase !== 'collecting') return;

      let nextIndex = currentIndex + 1;

      while (
        nextIndex < steps.length &&
        steps[nextIndex].skipIf?.(values)
      ) {
        nextIndex++;
      }

      if (nextIndex >= steps.length) {
        setTyping(true);
        typingTimeout.current = setTimeout(() => {
          setTyping(false);
          const reviewMsg = agentMsg(
            '✅ Perfeito! Coletei todas as informações necessárias. Você pode revisar o formulário ao lado e fazer ajustes se precisar.',
          );
          const doneMsg = agentMsg(
            'Use os botões abaixo para **exportar o JSON**, **limpar** ou **reiniciar** a conversa.',
            {
              quickReplies: [
                { label: 'Exportar JSON', value: '__export__' },
                { label: 'Recomeçar', value: '__restart__' },
              ],
            },
          );
          setState((prev) => {
            const formValues = normalizeAgentValues(prev.selectedTemplateId ?? '', prev.formValues);
            const fieldErrors = validateAgentValues(prev.selectedTemplateId ?? '', formValues);
            const hasErrors = Object.keys(fieldErrors).length > 0;
            return {
              ...prev,
              formValues,
              fieldErrors,
              phase: hasErrors ? 'review' : 'complete',
              currentStepIndex: nextIndex,
              isTyping: false,
              messages: [...prev.messages, ...(hasErrors
                ? [agentMsg('⚠️ Revise os campos destacados no formulário antes de exportar o JSON.\n\n' + Array.from(new Set(Object.values(fieldErrors))).join('\n'))]
                : [reviewMsg, doneMsg])],
            };
          });
        }, TYPING_DELAY);
        return;
      }

      const nextStep = steps[nextIndex];
      setTyping(true);
      typingTimeout.current = setTimeout(() => {
        setTyping(false);
        const nextMsg = agentMsg(nextStep.question, {
          fieldKey: nextStep.fieldKey,
          quickReplies: nextStep.quickReplies,
        });
        setState((prev) => ({
          ...prev,
          currentStepIndex: nextIndex,
          isTyping: false,
          messages: [...prev.messages, nextMsg],
        }));
      }, TYPING_DELAY);
    },
    [setTyping],
  );

  const handleUserInput = useCallback(
    (input: string) => {
      if (!input.trim()) return;

      setState((prev) => {
        const { phase, formValues, currentStepIndex, steps } = prev;

        if (phase === 'greeting' || phase === 'template_selection') {
          const template = templates.find((t) => t.id === input || t.name === input);
          if (!template) {
            const msg = agentMsg('Não reconheci esse template. Selecione uma das opções abaixo:', {
              quickReplies: templates.map((t) => ({ label: `${t.number} — ${t.name}`, value: t.id })),
            });
            return { ...prev, messages: [...prev.messages, userMsg(input), msg] };
          }

          const steps = templateSteps[template.id] ?? [];
          const firstStep = steps[0];
          const confirmMsg = agentMsg(
            `Ótima escolha! Vamos criar uma promoção do tipo **${template.name}** (${template.mechanicsBadge}).\n\nVou te fazer algumas perguntas. Pode digitar ou usar os botões de resposta rápida.`,
          );
          const firstQuestion = agentMsg(firstStep.question, {
            fieldKey: firstStep.fieldKey,
            quickReplies: firstStep.quickReplies,
          });

          return {
            ...prev,
            phase: 'collecting' as ConversationPhase,
            selectedTemplateId: template.id,
            steps,
            currentStepIndex: 0,
            messages: [...prev.messages, userMsg(input), confirmMsg, firstQuestion],
          };
        }

        if (phase === 'collecting') {
          const step = steps[currentStepIndex];
          if (!step) return prev;

          const trimmed = input.trim();

          if (step.isDateTime) {
            const startVal = step.fieldKey === 'inicioVigencia' ? trimmed : (formValues['inicioVigencia'] as string) ?? '';
            const endVal = step.fieldKey === 'fimVigencia' ? trimmed : (formValues['fimVigencia'] as string) ?? '';
            const { startError, endError } = validateDates(startVal, endVal);
            const error = step.fieldKey === 'inicioVigencia' ? startError : endError;
            if (error) {
              const errorMsg = agentMsg(`⚠️ ${error} Por favor, informe uma data válida.`);
              return { ...prev, messages: [...prev.messages, userMsg(trimmed, step.fieldKey), errorMsg] };
            }
          }

          const isEmpty = EMPTY_FIELDS.includes(trimmed.toLowerCase());
          let newValues: FormValues = { ...formValues };

          if (!isEmpty) {
            if (step.multiple) {
              newValues[step.fieldKey] = trimmed.split(',').map((v) => v.trim()).filter(Boolean);
            } else {
              newValues[step.fieldKey] = trimmed;
            }
          } else {
            newValues[step.fieldKey] = step.multiple ? [] : '';
          }

          newValues = normalizeAgentValues(prev.selectedTemplateId ?? '', newValues);
          const answerError = getAgentAnswerError(prev.selectedTemplateId ?? '', step.fieldKey, newValues);
          if (answerError) {
            return {
              ...prev,
              fieldErrors: { ...prev.fieldErrors, [step.fieldKey]: answerError },
              messages: [...prev.messages, userMsg(trimmed, step.fieldKey), agentMsg(`⚠️ ${answerError} Corrija a resposta ou o título no formulário ao lado.`)],
            };
          }

          const displayText = resolveDisplayText(trimmed, step);
          const uMsg = userMsg(displayText, step.fieldKey);
          const newMessages = [...prev.messages, uMsg];
          const newState: ConversationState = {
            ...prev,
            formValues: newValues,
            fieldErrors: {},
            messages: newMessages,
            isTyping: true,
          };

          pendingAdvance.current = { currentIndex: currentStepIndex, steps, values: newValues };
          return newState;
        }

        if (phase === 'complete' || phase === 'review') {
          if (input === '__restart__') {
            return {
              phase: 'greeting',
              messages: GREETING_MESSAGES,
              selectedTemplateId: null,
              formValues: { tipoSolicitacao: '', idIFC: 'n/a' },
              currentStepIndex: 0,
              steps: [],
              isTyping: false,
              fieldErrors: {},
            };
          }
          if (input === '__export__') {
            return prev;
          }
        }

        return prev;
      });
    },
    [advanceToNextStep],
  );

  useEffect(() => {
    if (!state.isTyping || !pendingAdvance.current) return;
    const { currentIndex, steps, values } = pendingAdvance.current;
    pendingAdvance.current = null;
    advanceToNextStep(currentIndex, steps, values, 'collecting');
  }, [state.isTyping, advanceToNextStep]);

  const exportJSON = useCallback(() => {
    const { selectedTemplateId } = state;
    if (!selectedTemplateId || !['complete', 'review'].includes(state.phase)) return;
    const formValues = normalizeAgentValues(selectedTemplateId, state.formValues);
    const fieldErrors = validateAgentValues(selectedTemplateId, formValues);
    if (Object.keys(fieldErrors).length > 0) {
      setState((prev) => ({
        ...prev,
        formValues,
        fieldErrors,
        phase: 'review',
        messages: [...prev.messages, agentMsg('⚠️ Não foi possível exportar. Corrija os campos destacados no formulário.\n\n' + Array.from(new Set(Object.values(fieldErrors))).join('\n'))],
      }));
      return;
    }
    const data = { templateId: selectedTemplateId, ...formValues };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `promocao-${selectedTemplateId ?? 'briefing'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [state]);

  const handleFieldChange = useCallback((key: string, value: string | string[]) => {
    setState((prev) => {
      const formValues = normalizeAgentValues(prev.selectedTemplateId ?? '', { ...prev.formValues, [key]: value });
      const reviewing = prev.phase === 'complete' || prev.phase === 'review';
      const fieldErrors = reviewing ? validateAgentValues(prev.selectedTemplateId ?? '', formValues) : {};
      return {
        ...prev,
        formValues,
        fieldErrors,
        phase: reviewing ? (Object.keys(fieldErrors).length ? 'review' : 'complete') : prev.phase,
      };
    });
  }, []);

  const restart = useCallback(() => {
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    pendingAdvance.current = null;
    setState({
      phase: 'greeting', messages: GREETING_MESSAGES, selectedTemplateId: null,
      formValues: { tipoSolicitacao: '', idIFC: 'n/a' }, currentStepIndex: 0,
      steps: [], isTyping: false, fieldErrors: {},
    });
  }, []);

  useEffect(() => () => {
    if (typingTimeout.current) clearTimeout(typingTimeout.current);
  }, []);

  const selectedTemplate = templates.find((t) => t.id === state.selectedTemplateId) ?? null;
  const activeSteps = state.steps.filter((step) => !step.skipIf?.(state.formValues));
  const completedSteps = state.steps.slice(0, state.currentStepIndex)
    .filter((step) => !step.skipIf?.(state.formValues));
  const progress = activeSteps.length ? Math.round((completedSteps.length / activeSteps.length) * 100) : 0;

  return {
    state,
    selectedTemplate,
    progress,
    handleUserInput,
    handleFieldChange,
    exportJSON,
    restart,
  };
}

